import { useState } from 'react';
import { Map as MapIcon, MapPin, Layers, ChevronLeft, Store, Compass, Radar } from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { useAuth } from '@/context/AuthContext';
import { usePost } from '@/lib/useData';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { CityMap, transfoColor } from '@/components/geo/CityMap';
import { ProspectionView } from '@/components/geo/ProspectionView';
import { SpotScanner } from '@/components/geo/SpotScanner';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

// ---- Region view (kept from the original stylised territory map) -------------
const COORDS = {
  'Île-de-France': { x: 52, y: 34 },
  'Centre-Val de Loire': { x: 45, y: 47 },
  'Auvergne-Rhône-Alpes': { x: 60, y: 62 },
  'Nouvelle-Aquitaine / Occitanie': { x: 34, y: 76 },
};

function coordFor(name) {
  if (COORDS[name]) return COORDS[name];
  const n = (name || '').toLowerCase();
  if (n.includes('itin')) return null;
  if (n.includes('france') || n.includes('idf') || n.includes('paris')) return COORDS['Île-de-France'];
  if (n.includes('aquitaine') || n.includes('occitanie')) return COORDS['Nouvelle-Aquitaine / Occitanie'];
  if (n.includes('auvergne') || n.includes('rhône') || n.includes('rhone') || n.includes('clermont')) return COORDS['Auvergne-Rhône-Alpes'];
  if (n.includes('centre') || n.includes('dreux')) return COORDS['Centre-Val de Loire'];
  return null;
}

export default function Carte() {
  const { body, clearAll } = useFilters();
  const { isAdmin } = useAuth();
  const [view, setView] = useState('villes');
  const [selected, setSelected] = useState(null);

  return (
    <div className="space-y-6">
      <PageHeader title="Carte" icon={MapIcon} subtitle="Performance géographique — par ville et par territoire" />

      <Tabs value={view} onValueChange={(v) => { setView(v); setSelected(null); }}>
        <TabsList data-testid="carte-tabs">
          <TabsTrigger value="villes" data-testid="carte-tab-villes" className="gap-1.5">
            <MapPin className="h-4 w-4" /> Villes
          </TabsTrigger>
          <TabsTrigger value="territoires" data-testid="carte-tab-territoires" className="gap-1.5">
            <Layers className="h-4 w-4" /> Territoires
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="prospection" data-testid="carte-tab-prospection" className="gap-1.5">
              <Compass className="h-4 w-4" /> Prospection
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="analyser" data-testid="carte-tab-analyser" className="gap-1.5">
              <Radar className="h-4 w-4" /> Analyser un spot
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="villes" className="mt-4">
          <CitiesView body={body} clearAll={clearAll} selected={selected} setSelected={setSelected} />
        </TabsContent>

        <TabsContent value="territoires" className="mt-4">
          <RegionsView body={body} clearAll={clearAll} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="prospection" className="mt-4">
            <ProspectionView body={body} />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="analyser" className="mt-4">
            <SpotScanner body={body} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ---- City view (real interactive Leaflet map) --------------------------------
function CitiesView({ body, clearAll, selected, setSelected }) {
  const { data, isLoading, isError, refetch } = usePost('/analytics/map/cities', body);

  if (isLoading) return <CardSkeleton height="h-[540px]" />;
  if (isError) return <ErrorState body="Impossible de charger la carte des villes." onRetry={refetch} />;

  const cities = data?.cities || [];
  const unlocated = data?.unlocated || [];
  if (!cities.length) return <EmptyState onClear={clearAll} />;

  const sel = selected ? cities.find((c) => c.city === selected) : null;
  const pct = Math.round((data?.located_pct || 0) * 100);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        {/* Map */}
        <div className="space-y-2 xl:col-span-3">
          <CityMap cities={cities} onSelect={(c) => setSelected(c.city)} selectedCity={selected} />
          <p className="text-xs text-muted-foreground break-words">
            {fmtInt(data.total_cities)} villes géolocalisées · <strong className="text-foreground">{pct}%</strong> du volume localisé.
            Cliquez une ville pour voir ses spots.
          </p>
        </div>

        {/* Side panel: drill-down or top cities */}
        <div className="xl:col-span-2">
          {sel ? (
            <SectionCard
              title={sel.city}
              subtitle={`${fmtInt(sel.nb_spots)} spot(s) · ${fmtInt(sel.bs_reel)} BS validés`}
              testid="city-detail"
              action={
                <Button variant="ghost" size="sm" className="gap-1" onClick={() => setSelected(null)} data-testid="city-detail-back">
                  <ChevronLeft className="h-4 w-4" /> Retour
                </Button>
              }
            >
              <div className="mb-3 grid grid-cols-2 gap-2">
                <MiniStat label="Transformation" value={fmtPct(sel.taux_transfo)} />
                <MiniStat label="Don moyen" value={fmtEur(sel.don_moyen)} />
                <MiniStat label="Âge moyen" value={`${fmtNum(sel.age_moyen, 1)} ans`} />
                <MiniStat label="Jours actifs" value={fmtInt(sel.jours_actifs)} />
              </div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                <Store className="h-3.5 w-3.5" /> Spots de la ville
              </p>
              <div className="space-y-2">
                {sel.spots.map((sp) => (
                  <div key={sp.lieu} className="rounded-xl border bg-card p-2.5" data-testid="city-spot-row">
                    <div className="flex items-start justify-between gap-2">
                      <span className="break-words text-sm font-medium">{sp.lieu}</span>
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: transfoColor(sp.taux_transfo), marginTop: 5 }} />
                    </div>
                    <div className="mt-1.5 grid grid-cols-3 gap-1 text-xs">
                      <div><p className="text-muted-foreground">BS</p><p className="font-medium tabular">{fmtInt(sp.bs_reel)}</p></div>
                      <div><p className="text-muted-foreground">Transfo</p><p className="font-medium tabular">{fmtPct(sp.taux_transfo)}</p></div>
                      <div><p className="text-muted-foreground">Don</p><p className="font-medium tabular">{fmtEur(sp.don_moyen)}</p></div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          ) : (
            <SectionCard title="Top villes" subtitle="Classées par BS validés · cliquez pour zoomer" testid="city-top-list">
              <div className="space-y-2">
                {cities.slice(0, 15).map((c, i) => (
                  <button
                    key={c.city}
                    type="button"
                    onClick={() => setSelected(c.city)}
                    className="flex w-full items-center justify-between gap-2 rounded-xl border bg-card p-2.5 text-left transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-[color:var(--tenant-accent-300)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-400)]"
                    data-testid="city-list-row"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="text-xs font-semibold text-muted-foreground tabular">{i + 1}</span>
                      <span className="break-words text-sm font-medium">{c.city}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="text-sm font-semibold tabular">{fmtInt(c.bs_reel)}</span>
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: transfoColor(c.taux_transfo) }} />
                    </span>
                  </button>
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      </div>

      {/* Unlocated spots */}
      {unlocated.length > 0 && (
        <SectionCard
          title="Spots non localisés"
          subtitle={`${fmtInt(unlocated.length)} lieu(x) non rattaché(s) à une commune (ex. quartiers, centres) — inclus dans les totaux`}
          testid="city-unlocated"
        >
          <div className="flex flex-wrap gap-2">
            {unlocated.slice(0, 40).map((u) => (
              <span key={u.lieu} className="inline-flex items-center gap-1.5 rounded-lg border bg-muted/40 px-2.5 py-1 text-xs" data-testid="unlocated-chip">
                <span className="break-words font-medium">{u.lieu}</span>
                <span className="text-muted-foreground">· {fmtInt(u.bs_reel)} BS</span>
              </span>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

const MiniStat = ({ label, value }) => (
  <div className="rounded-xl border bg-card p-2.5">
    <p className="text-[11px] text-muted-foreground break-words">{label}</p>
    <p className="mt-0.5 font-display text-base font-semibold tabular text-foreground break-words">{value}</p>
  </div>
);

// ---- Region view (stylised, kept for continuity) -----------------------------
function RegionsView({ body, clearAll }) {
  const { data, isLoading, isError, refetch } = usePost('/analytics/map', body);
  if (isLoading) return <CardSkeleton height="h-96" />;
  if (isError) return <ErrorState body="Impossible de charger la vue territoires." onRetry={refetch} />;

  const items = data?.items?.items || data?.items || [];
  if (!items.length) return <EmptyState onClear={clearAll} />;
  const maxBs = Math.max(1, ...items.map((i) => i.bs_reel));
  const mapped = items.filter((i) => coordFor(i.key));
  const unmapped = items.filter((i) => !coordFor(i.key));

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
      <SectionCard title="Cartographie des territoires" subtitle="Taille = volume · couleur = taux de transformation" className="xl:col-span-3">
        <div className="relative mx-auto w-full max-w-md">
          <svg viewBox="0 0 100 110" className="w-full">
            <path d="M50 6 C64 8 76 14 82 26 C88 36 86 46 90 56 C94 66 88 76 78 84 C70 92 60 98 50 100 C40 98 30 92 22 84 C12 76 6 66 10 56 C14 46 12 36 18 26 C24 14 36 8 50 6 Z"
              fill="var(--tenant-accent-050)" stroke="var(--tenant-accent-100)" strokeWidth="0.8" />
            {mapped.map((it) => {
              const c = coordFor(it.key);
              const r = 3 + (it.bs_reel / maxBs) * 9;
              return (
                <g key={it.key}>
                  <circle cx={c.x} cy={c.y} r={r} fill={transfoColor(it.taux_transfo)} fillOpacity="0.28" />
                  <circle cx={c.x} cy={c.y} r={r * 0.55} fill={transfoColor(it.taux_transfo)}>
                    <title>{`${it.key} — ${fmtInt(it.bs_reel)} BS validés — ${fmtPct(it.taux_transfo)}`}</title>
                  </circle>
                  <text x={c.x} y={c.y - r - 1.5} textAnchor="middle" fontSize="3.2" fill="#0B1320" fontWeight="600">{it.key}</text>
                </g>
              );
            })}
          </svg>
        </div>
        {unmapped.length > 0 && (
          <p className="mt-2 text-center text-xs text-muted-foreground break-words">
            {unmapped.length} territoire(s) non localisé(s) affiché(s) dans le tableau.
          </p>
        )}
      </SectionCard>

      <SectionCard title="Détail par territoire" subtitle={items.length > 30 ? `Top 30 sur ${fmtInt(items.length)} territoires` : undefined} className="xl:col-span-2">
        <div className="space-y-2">
          {items.slice(0, 30).map((it) => (
            <div key={it.key} className="rounded-xl border bg-card p-3" data-testid="map-region-row">
              <div className="flex items-center justify-between">
                <span className="break-words text-sm font-semibold">{it.key}</span>
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: transfoColor(it.taux_transfo) }} />
              </div>
              <div className="mt-2 grid grid-cols-4 gap-1 text-xs">
                <div><p className="text-muted-foreground">BS</p><p className="font-medium tabular">{fmtInt(it.bs_reel)}</p></div>
                <div><p className="text-muted-foreground">Transfo</p><p className="font-medium tabular">{fmtPct(it.taux_transfo)}</p></div>
                <div><p className="text-muted-foreground">Don</p><p className="font-medium tabular">{fmtEur(it.don_moyen)}</p></div>
                <div><p className="text-muted-foreground">Âge</p><p className="font-medium tabular">{fmtNum(it.age_moyen, 0)}</p></div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
